import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { DateTime } from 'luxon'
import { useCallback, useEffect, useRef } from 'react'
import {
  SecondaryTextContainer,
  TabListTitle
} from '../../../../components/stories/organisms/top-navigation/components/global-search/styled-components'
import GlobalSearchNotFound from '../../../../components/stories/organisms/top-navigation/components/global-search/components/not-found'
import ContextMenuComponent from '../../../../components/stories/organisms/context-menu/index.jsx'
import HighlightTitle from '../../../../components/stories/organisms/top-navigation/components/global-search/components/highligh-title.jsx'
import PinComponent from '../../../../components/stories/atoms/pins/index.jsx'
import { pinsChildrenSizeOptions } from '../../../../components/stories/atoms/pins/constants.js'
import { handleIconIndicator } from '../../../Plan/components/Pin.js'
import {
  badgeSizeOptions,
  badgesVariantOptions
} from '../../../../components/stories/atoms/badges/constants.js'
import BadgesComponent from '../../../../components/stories/atoms/badges/index.jsx'
import { useOnInView } from 'react-intersection-observer'
import QuickSelectComponent from '../../../../components/stories/organisms/top-navigation/components/quick-select/index.jsx'
import TopBarLoadingContent from '../../../../components/stories/organisms/top-navigation/components/loading-content.jsx'
import { FormattedMessage } from 'react-intl'

const CustomPinsTab = ({ search, pinsQuery = null, handleNavigatePin }) => {
  const { data, hasNextPage, fetchNextPage, isFetchingNextPage, isLoading } =
    pinsQuery

  const allPins = data?.pages.flatMap((d) => d.content) || []

  const onBottomReached = useCallback(() => {
    hasNextPage && !isFetchingNextPage && fetchNextPage()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const onBottomReachedRef = useRef(onBottomReached)

  useEffect(() => {
    onBottomReachedRef.current = onBottomReached
  }, [onBottomReached])

  const bottomReachedRef = useOnInView(
    useCallback((inView) => {
      if (inView) {
        onBottomReachedRef.current?.()
      }
    }, []),
    {
      rootMargin: '20px',
      skip: !hasNextPage
    }
  )

  const SecondaryText = ({ pinNumber, lastModified, planName }) => {
    const relativeTime = DateTime.fromISO(lastModified).toRelative()

    return (
      <SecondaryTextContainer>
        #{pinNumber} • {relativeTime} • {planName}
      </SecondaryTextContainer>
    )
  }

  if (isLoading) {
    return <TopBarLoadingContent />
  }

  if (isEmpty(allPins)) {
    return <GlobalSearchNotFound />
  }

  return (
    <>
      {!search && (
        <TabListTitle>
          <FormattedMessage
            id="recently_updated"
            style={{
              textTransform: 'uppercase'
            }}
          />
        </TabListTitle>
      )}
      {allPins.map((pin, index) => (
        <ContextMenuComponent.Item
          key={pin.id}
          id={pin.id}
          title={<HighlightTitle title={pin.title} query={search} />}
          secondaryText={
            <SecondaryText
              pinNumber={pin.location.number}
              lastModified={pin.lastModified}
              planName={pin.planReference.planName}
            />
          }
          leadingIcon={
            <PinComponent
              variant={`gradient-${pin.location.color.toLowerCase()}`}
              size={27}
              isInactive={
                pin.location.state && pin.location.state === 'Inactive'
              }
              scale={pinsChildrenSizeOptions.progress.scale}
              translate={pinsChildrenSizeOptions.progress.translate}
              percentage={
                pin.progress.totalTasks > 0 ? pin.location.progress : null
              }
              iconStatus={
                pin.progress.totalTasks > 0
                  ? handleIconIndicator(
                      pin.location.state,
                      pin.location.indicatorCurrent,
                      pin.location.progress
                    )
                  : null
              }
            />
          }
          badge={
            <BadgesComponent
              color={pin.location.color.toLowerCase()}
              variant={badgesVariantOptions.STRONG}
              size={badgeSizeOptions.SMALL}
            >
              {pin.category.name}
            </BadgesComponent>
          }
          style={{ width: '100%', padding: '0.25rem 0.5rem' }}
          onClick={() => handleNavigatePin(pin)}
        />
      ))}
      {isFetchingNextPage && <QuickSelectComponent.LoadingItem />}

      <div
        ref={bottomReachedRef}
        style={{
          height: '1px'
        }}
      />
    </>
  )
}

CustomPinsTab.propTypes = {
  search: PropTypes.string,
  projectId: PropTypes.string
}

export default CustomPinsTab
